/**
 * SoundManager - Sistema de efeitos sonoros para BelottoPokeClass
 * Usa Web Audio API para gerar sons sintetizados (sem arquivos externos)
 * 
 * Sons disponíveis:
 * - menuOpen: Som de abertura da mochila (clique metálico)
 * - menuClose: Som de fechamento (zíper suave)
 * - itemReveal: Fanfarra de revelação de item
 * - itemRevealRare: Fanfarra especial para itens raros
 * - itemUse: Som de uso de item (brilho mágico)
 * - healEffect: Som de cura (HP/Energia)
 * - levelUp: Som de level up (Rare Candy)
 * - buttonClick: Som de clique em botão
 */

class SoundManager {
  constructor() {
    this.audioContext = null;
    this.isMuted = this.loadMuteState();
    this.volume = 0.5; // 50% volume default
    this.isInitialized = false;
  }

  // Initialize AudioContext (must be called after user interaction)
  init() {
    if (this.isInitialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.isInitialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported');
    }
  }

  // Load mute state from localStorage
  loadMuteState() {
    try {
      const saved = localStorage.getItem('belotto-pokeclass-sound-muted');
      return saved === 'true';
    } catch {
      return false;
    }
  }

  // Save mute state to localStorage
  saveMuteState() {
    try {
      localStorage.setItem('belotto-pokeclass-sound-muted', this.isMuted.toString());
    } catch (e) {
      console.warn('localStorage not available for sound state:', e);
    }
  }

  // Toggle mute state
  toggleMute() {
    this.isMuted = !this.isMuted;
    this.saveMuteState();
    return this.isMuted;
  }

  // Set mute state
  setMuted(muted) {
    this.isMuted = muted;
    this.saveMuteState();
  }

  // Get mute state
  getMuted() {
    return this.isMuted;
  }

  // Create oscillator with envelope
  createTone(frequency, type = 'sine', duration = 0.1, startTime = 0) {
    if (!this.audioContext || this.isMuted) return null;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime + startTime);

    const attackTime = 0.01;
    const releaseTime = duration * 0.3;
    const peakTime = this.audioContext.currentTime + startTime + attackTime;
    const endTime = this.audioContext.currentTime + startTime + duration;

    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime + startTime);
    gainNode.gain.linearRampToValueAtTime(this.volume * 0.15, peakTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, endTime);

    oscillator.start(this.audioContext.currentTime + startTime);
    oscillator.stop(endTime + 0.1);

    return { oscillator, gainNode };
  }

  // ============ SOUND EFFECTS ============

  /**
   * Menu Open Sound - Metallic click with resonance
   * Simulates opening a backpack/inventory
   */
  playMenuOpen() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // High metallic ping
    this.createTone(1200, 'sine', 0.08, 0);
    this.createTone(800, 'triangle', 0.12, 0.02);
    
    // Lower resonance
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.15);
    
    gain.gain.setValueAtTime(this.volume * 0.08, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.15);
    
    osc.start();
    osc.stop(this.audioContext.currentTime + 0.2);
  }

  /**
   * Menu Close Sound - Soft zipper/click
   */
  playMenuClose() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Descending click
    this.createTone(600, 'sine', 0.06, 0);
    this.createTone(400, 'triangle', 0.08, 0.02);
  }

  /**
   * Item Reveal Sound - Fanfare for common items
   * Short triumphant melody (< 2 seconds)
   */
  playItemReveal() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Ascending arpeggio: C5 - E5 - G5 - C6
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.25, i * 0.12);
    });

    // Sparkle effect
    setTimeout(() => {
      this.createTone(2000, 'sine', 0.1, 0);
      this.createTone(2400, 'sine', 0.08, 0.05);
      this.createTone(2800, 'sine', 0.06, 0.1);
    }, 400);
  }

  /**
   * Item Reveal Rare Sound - Epic fanfare for rare items
   * More elaborate with harmonics (< 3 seconds)
   */
  playItemRevealRare() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Epic ascending chord progression
    const chords = [
      [523, 659, 784],      // C major
      [587, 740, 880],      // D major
      [659, 830, 988],      // E major
      [784, 988, 1175],     // G major
      [1047, 1319, 1568]    // C major (octave up)
    ];

    chords.forEach((chord, i) => {
      chord.forEach((freq, j) => {
        this.createTone(freq, 'sine', 0.35, i * 0.15 + j * 0.02);
      });
    });

    // Golden sparkle cascade
    setTimeout(() => {
      for (let i = 0; i < 8; i++) {
        const freq = 2000 + (i * 200);
        this.createTone(freq, 'sine', 0.08, i * 0.06);
      }
    }, 700);

    // Final triumphant note
    setTimeout(() => {
      this.createTone(1047, 'sine', 0.5, 0);
      this.createTone(1319, 'sine', 0.5, 0);
      this.createTone(1568, 'sine', 0.5, 0);
    }, 1200);
  }

  /**
   * Item Use Sound - Magical shimmer/sparkle
   * Plays when clicking "Usar" button
   */
  playItemUse() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Rising shimmer effect
    const shimmerFreqs = [800, 1000, 1200, 1400, 1600];
    shimmerFreqs.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.15, i * 0.05);
    });

    // Magical chime
    setTimeout(() => {
      this.createTone(2093, 'sine', 0.2, 0); // C7
      this.createTone(2637, 'sine', 0.15, 0.08); // E7
    }, 200);
  }

  /**
   * Heal Effect Sound - Soft healing/restoration
   * For Potion and Berry usage
   */
  playHealEffect() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Gentle ascending tones
    const healNotes = [440, 554, 659, 880];
    healNotes.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.2, i * 0.1);
    });

    // Soft sparkle
    setTimeout(() => {
      this.createTone(1760, 'sine', 0.1, 0);
      this.createTone(2093, 'sine', 0.08, 0.05);
    }, 350);
  }

  /**
   * Level Up Sound - Epic level up fanfare
   * For Rare Candy usage
   */
  playLevelUp() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Dramatic ascending scale
    const scale = [523, 587, 659, 698, 784, 880, 988, 1047];
    scale.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.12, i * 0.08);
    });

    // Triumphant chord
    setTimeout(() => {
      this.createTone(1047, 'sine', 0.4, 0);
      this.createTone(1319, 'sine', 0.4, 0);
      this.createTone(1568, 'sine', 0.4, 0);
      this.createTone(2093, 'sine', 0.3, 0.1);
    }, 600);

    // Sparkle flourish
    setTimeout(() => {
      for (let i = 0; i < 5; i++) {
        this.createTone(2000 + i * 300, 'sine', 0.06, i * 0.04);
      }
    }, 900);
  }

  /**
   * Button Click Sound - Simple UI feedback
   */
  playButtonClick() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    this.createTone(800, 'sine', 0.05, 0);
    this.createTone(600, 'sine', 0.03, 0.02);
  }

  /**
   * Tab Switch Sound - Soft click for switching tabs
   */
  playTabSwitch() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    this.createTone(700, 'sine', 0.04, 0);
    this.createTone(900, 'sine', 0.03, 0.015);
  }

  /**
   * Error Sound - Gentle error/blocked action
   */
  playError() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    this.createTone(300, 'sawtooth', 0.1, 0);
    this.createTone(250, 'sawtooth', 0.15, 0.08);
  }

  // ============ REAL-TIME STAT CHANGE SOUNDS ============

  /**
   * Energy Recharge Sound - Power-up / Battery charging
   * Cristalino e curto - para quando professor dá energia
   */
  playEnergyRecharge() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Electric charging sound - ascending with sparkle
    const chargingNotes = [330, 440, 554, 659, 784, 880];
    chargingNotes.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.12, i * 0.06);
    });

    // Electric crackle/sparkle at the end
    setTimeout(() => {
      for (let i = 0; i < 4; i++) {
        this.createTone(1500 + i * 300, 'triangle', 0.05, i * 0.03);
      }
    }, 350);

    // Final power-up chime
    setTimeout(() => {
      this.createTone(1047, 'sine', 0.15, 0);
      this.createTone(1319, 'sine', 0.12, 0.03);
    }, 450);
  }

  /**
   * HP Heal Sound - Soft healing from professor
   * Verde/suave - restauração de vida
   */
  playHPHeal() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Warm, gentle healing tones
    const healTones = [392, 494, 587, 698]; // G4-B4-D5-F5
    healTones.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.2, i * 0.12);
    });

    // Soft glow effect
    setTimeout(() => {
      this.createTone(880, 'sine', 0.15, 0);
      this.createTone(1047, 'sine', 0.1, 0.05);
    }, 450);
  }

  /**
   * HP Damage Sound - Damage/penalty from professor
   * Impactante mas não assustador
   */
  playHPDamage() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Low impact thud
    this.createTone(150, 'sawtooth', 0.15, 0);
    this.createTone(120, 'sawtooth', 0.2, 0.05);
    
    // Descending warning tone
    setTimeout(() => {
      this.createTone(400, 'triangle', 0.1, 0);
      this.createTone(300, 'triangle', 0.12, 0.06);
    }, 100);
  }

  /**
   * XP Gain Sound - Experience points received
   * Positivo e motivador
   */
  playXPGain() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Bright, cheerful ascending
    const xpNotes = [523, 659, 784]; // C5-E5-G5
    xpNotes.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.15, i * 0.08);
    });

    // Sparkle flourish
    setTimeout(() => {
      this.createTone(1568, 'sine', 0.08, 0);
      this.createTone(1760, 'sine', 0.06, 0.04);
    }, 250);
  }

  /**
   * XP Loss Sound - XP penalty
   */
  playXPLoss() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Descending disappointed tone
    this.createTone(400, 'sine', 0.1, 0);
    this.createTone(350, 'sine', 0.12, 0.06);
    this.createTone(300, 'sine', 0.14, 0.12);
  }

  /**
   * Evolution Sound - Pokemon evolution triggered
   * Épico e mágico
   */
  playEvolution() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Mystical ascending scale
    const evolutionScale = [262, 330, 392, 523, 659, 784, 1047];
    evolutionScale.forEach((freq, i) => {
      this.createTone(freq, 'sine', 0.25, i * 0.1);
    });

    // Magical transformation chord
    setTimeout(() => {
      [1047, 1319, 1568, 2093].forEach((freq, i) => {
        this.createTone(freq, 'sine', 0.4, i * 0.03);
      });
    }, 700);

    // Sparkle cascade
    setTimeout(() => {
      for (let i = 0; i < 6; i++) {
        this.createTone(2000 + i * 200, 'sine', 0.06, i * 0.05);
      }
    }, 1100);
  }

  /**
   * Notification Alert Sound - When receiving notification
   * Sutil mas perceptível
   */
  playNotificationAlert() {
    this.init();
    if (!this.audioContext || this.isMuted) return;

    // Gentle two-tone alert
    this.createTone(880, 'sine', 0.1, 0);
    this.createTone(1047, 'sine', 0.08, 0.08);
  }
}

// Singleton instance
const soundManager = new SoundManager();

export default soundManager;
